import React, { useEffect, useState, useCallback } from 'react';
import api from '../utils/api';
import { Search, Plus, Phone, MessageCircle, ChevronRight, Users, CheckSquare, Square, UserCheck } from 'lucide-react';
import CallButton from '../components/CallButton';
import toast from 'react-hot-toast';
import LeadDetail from './LeadDetail';
import AddLeadModal from '../components/AddLeadModal';
import CSVUploadModal from '../components/CSVUploadModal';
import { useAuth } from '../context/AuthContext';

const STAGES = ['New Lead','Initial Discussion','Credit Report Pending','Credit Report Received','Analysis Shared','Lender Confirmation','Proposal Sent','Enrollment Follow-up','Payment Expected'];

function stageBadge(stage) {
  const map = {
    'New Lead': 'badge-gray', 'Initial Discussion': 'badge-blue',
    'Credit Report Pending': 'badge-yellow', 'Credit Report Received': 'badge-blue',
    'Analysis Shared': 'badge-purple', 'Lender Confirmation': 'badge-purple',
    'Proposal Sent': 'badge-blue', 'Enrollment Follow-up': 'badge-yellow',
    'Payment Expected': 'badge-blue',
  };
  return map[stage] || 'badge-gray';
}

function probDot(p) { return p >= 70 ? 'high' : p >= 40 ? 'medium' : 'low'; }
function fmt(n) { return n ? '₹' + Number(n).toLocaleString('en-IN') : '—'; }
const H_LABELS = { not_now: 'Not Now', multiple_calls: 'Multiple Calls', threat_to_visit: 'Threat to Visit' };

// Bulk allocate modal
function BulkAllocateModal({ selectedIds, leads, onClose, onSave }) {
  const [users, setUsers] = useState([]);
  const [assignTo, setAssignTo] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get('/users').then(r => setUsers(r.data.filter(u => u.role === 'consultant' && u.active !== false))); }, []);

  const save = async () => {
    if (!assignTo) return toast.error('Consultant select karo');
    setSaving(true);
    try {
      await Promise.all(selectedIds.map(id => api.patch(`/leads/${id}`, { assignedTo: assignTo })));
      toast.success(`${selectedIds.length} leads allocated!`);
      onSave();
    } catch (e) {
      toast.error('Failed');
    } finally {
      setSaving(false);
    }
  };

  const selectedLeads = leads.filter(l => selectedIds.includes(l.id));

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 480 }}>
        <div className="modal-header">
          <div className="modal-title">🔀 Bulk Lead Allocation</div>
          <button className="btn-icon" onClick={onClose}><span>✕</span></button>
        </div>
        <div className="modal-body">
          <div style={{ marginBottom: 16, padding: '10px 12px', background: 'var(--surface)', borderRadius: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>{selectedIds.length} leads selected:</div>
            <div style={{ maxHeight: 150, overflowY: 'auto' }}>
              {selectedLeads.map(l => (
                <div key={l.id} style={{ fontSize: 12, padding: '3px 0', color: 'var(--text-muted)', borderBottom: '1px solid var(--border)' }}>
                  {l.name} — {l.mobile} — {l.stage}
                </div>
              ))}
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Assign to Consultant *</label>
            <select className="form-control" value={assignTo} onChange={e => setAssignTo(e.target.value)}>
              <option value="">Select consultant...</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? <span className="spinner" /> : `Allocate ${selectedIds.length} Leads`}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [stage, setStage] = useState('');
  const [selectedLead, setSelectedLead] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showCSV, setShowCSV] = useState(false);
  const [showAllocate, setShowAllocate] = useState(false);
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState([]); // bulk selection
  const [filterEmployee, setFilterEmployee] = useState('');
  const [showJunk, setShowJunk] = useState(false);
  const { user } = useAuth();

  const canAllocate = ['admin', 'tl'].includes(user?.role);

  const load = useCallback(() => {
    setLoading(true);
    setSelected([]);
    const params = { enrolled: 'false' };
    if (stage) params.stage = stage;
    if (search) params.search = search;
    if (filterEmployee) params.assignedTo = filterEmployee;
    if (showJunk) params.junk = 'true'; else params.junk = 'false';
    api.get('/leads', { params }).then(r => setLeads(r.data)).finally(() => setLoading(false));
  }, [stage, search, filterEmployee, showJunk]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get('/users').then(r => setUsers(r.data)).catch(() => {}); }, []);

  const toggleSelect = (id) => {
    setSelected(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  };

  const toggleAll = () => {
    setSelected(selected.length === leads.length ? [] : leads.map(l => l.id));
  };

  if (selectedLead) {
    return <LeadDetail leadId={selectedLead} onBack={() => { setSelectedLead(null); load(); }} users={users} />;
  }

  return (
    <div className="page">
      <div className="flex-between mb-24">
        <div>
          <h2 style={{ fontSize: 18 }}>Leads Pipeline</h2>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>{leads.length} active leads</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className={`btn btn-sm ${showJunk ? 'btn-danger' : 'btn-ghost'}`} onClick={() => setShowJunk(!showJunk)}>
            {showJunk ? '🗑 Junk Leads' : '🗑 Show Junk'}
          </button>
          {canAllocate && selected.length > 0 && (
            <button className="btn btn-gold" onClick={() => setShowAllocate(true)}>
              <UserCheck size={15} /> Allocate {selected.length} Leads
            </button>
          )}
          <button className="btn btn-ghost" onClick={() => setShowCSV(true)}>
            📤 CSV Upload
          </button>
          {canAllocate && (
            <button className="btn btn-ghost" onClick={() => setShowCSV(true)}>
              📤 Bulk Upload
            </button>
          )}
          <button className="btn btn-primary" onClick={() => setShowAdd(true)}>
            <Plus size={16} /> Add Lead
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-16" style={{ padding: '12px 16px' }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <div className="input-icon-wrap" style={{ flex: 1, minWidth: 220 }}>
            <Search size={15} />
            <input className="form-control" placeholder="Search name, mobile, email..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <select className="form-control" style={{ width: 220 }} value={stage} onChange={e => setStage(e.target.value)}>
            <option value="">All Stages</option>
            {STAGES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          {canAllocate && (
            <select className="form-control" style={{ width: 180 }} value={filterEmployee} onChange={e => setFilterEmployee(e.target.value)}>
              <option value="">👥 All Consultants</option>
              {users.filter(u => u.role === 'consultant').map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          )}
          {canAllocate && selected.length > 0 && (
            <span style={{ fontSize: 13, color: 'var(--gold)', fontWeight: 600 }}>{selected.length} selected</span>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="card" style={{ padding: 0 }}>
        <div className="table-wrap">
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
          ) : leads.length === 0 ? (
            <div className="empty-state"><Users size={40} /><h3>No leads found</h3><p>Add a lead or change filters</p></div>
          ) : (
            <table>
              <thead>
                <tr>
                  {canAllocate && (
                    <th style={{ width: 40 }}>
                      <button className="btn-icon" style={{ border: 'none', background: 'none' }} onClick={toggleAll}>
                        {selected.length === leads.length ? <CheckSquare size={15} style={{ color: 'var(--navy)' }} /> : <Square size={15} />}
                      </button>
                    </th>
                  )}
                  <th>Name</th>
                  <th>Mobile</th>
                  <th>City</th>
                  <th>Employment</th>
                  <th>Loan Details</th>
                  <th>Stage</th>
                  <th>Prob.</th>
                  <th>H</th>
                  <th>Promise</th>
                  <th>Assigned</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {leads.map(lead => (
                  <tr key={lead.id}
                    style={{ background: selected.includes(lead.id) ? '#eff6ff' : '', cursor: 'pointer' }}
                    onClick={() => setSelectedLead(lead.id)}
                  >
                    {canAllocate && (
                      <td onClick={e => e.stopPropagation()}>
                        <button className="btn-icon" style={{ border: 'none', background: 'none' }} onClick={e => { e.stopPropagation(); toggleSelect(lead.id); }}>
                          {selected.includes(lead.id) ? <CheckSquare size={15} style={{ color: 'var(--navy)' }} /> : <Square size={15} />}
                        </button>
                      </td>
                    )}
                    <td style={{ cursor: 'pointer' }}>
                      <div style={{ fontWeight: 600, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                        {lead.name}
                        {lead.enrolled && lead.promiseDate && new Date(lead.promiseDate) < new Date() && !lead.promiseFulfilled && (
                          <span title="Payment overdue" style={{ color: 'var(--danger)', fontSize: 14 }}>🔴</span>
                        )}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{lead.email}</div>
                    </td>
                    <td style={{ fontSize: 13 }}>{lead.mobile}</td>
                    <td style={{ fontSize: 13 }}>{lead.city || '—'}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{lead.employmentStatus || '—'}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {lead.personalLoan > 0 && (
                          <span style={{ fontSize: 12, padding: '2px 8px', background: '#eff6ff', borderRadius: 5, color: '#1d4ed8', fontWeight: 600 }}>
                            PL: {fmt(lead.personalLoan)}
                          </span>
                        )}
                        {lead.creditCard > 0 && (
                          <span style={{ fontSize: 12, padding: '2px 8px', background: '#fef3c7', borderRadius: 5, color: '#92400e', fontWeight: 600 }}>
                            CC: {fmt(lead.creditCard)}
                          </span>
                        )}
                        {lead.monthlyEmi > 0 && (
                          <span style={{ fontSize: 12, padding: '2px 8px', background: '#f0fdf4', borderRadius: 5, color: '#166534', fontWeight: 600 }}>
                            EMI: {fmt(lead.monthlyEmi)}
                          </span>
                        )}
                      </div>
                    </td>
                    <td><span className={`badge ${stageBadge(lead.stage)}`}>{lead.stage}</span></td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                        <span className={`prob-dot ${probDot(lead.probability)}`} />
                        <span style={{ fontSize: 12 }}>{lead.probability}%</span>
                      </div>
                    </td>
                    <td>
                      {lead.harassment
                        ? <span className={`h-dot ${lead.harassment}`} title={H_LABELS[lead.harassment]} />
                        : <span style={{ color: 'var(--text-light)', fontSize: 12 }}>—</span>}
                    </td>
                    <td>
                      {lead.promiseDate ? (
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 600 }}>{lead.promiseDate}</div>
                          {lead.promiseAmount && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{fmt(lead.promiseAmount)}</div>}
                        </div>
                      ) : <span style={{ color: 'var(--text-light)', fontSize: 12 }}>—</span>}
                    </td>
                    <td style={{ fontSize: 12 }}>{lead.assignedToName || '—'}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        {!showJunk && <>
                          <CallButton leadId={lead.id} mobile={lead.mobile} name={lead.name} size="small" showLabel={false} />
                          <a href={`https://wa.me/91${lead.mobile}`} target="_blank" rel="noreferrer" className="btn-icon" style={{ color: '#25d366' }} onClick={e => e.stopPropagation()}><MessageCircle size={13} /></a>
                          <button className="btn-icon" onClick={() => setSelectedLead(lead.id)}><ChevronRight size={13} /></button>
                          <button className="btn-icon" title="Mark Junk" style={{ color: 'var(--danger)' }}
                            onClick={e => { e.stopPropagation(); if(window.confirm('Mark as junk?')) api.post(`/leads/${lead.id}/junk`, { reason: 'Not responding' }).then(() => { toast.success('Marked junk'); load(); }); }}>
                            🗑
                          </button>
                        </>}
                        {showJunk && (
                          <button className="btn btn-success btn-sm"
                            onClick={e => { e.stopPropagation(); api.post(`/leads/${lead.id}/revive`).then(() => { toast.success('Lead revived!'); load(); }); }}>
                            ♻️ Revive
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showAdd && <AddLeadModal onClose={() => setShowAdd(false)} onSave={() => { setShowAdd(false); load(); toast.success('Lead added!'); }} users={users} />}
      {showAllocate && <BulkAllocateModal selectedIds={selected} leads={leads} onClose={() => setShowAllocate(false)} onSave={() => { setShowAllocate(false); load(); }} />}
      {showCSV && <CSVUploadModal users={users} onClose={() => setShowCSV(false)} onSave={() => { setShowCSV(false); load(); }} />}
    </div>
  );
}
